import {ArchitectureBaseConfig} from './diagram-architecture-base-config';
import {
  ActivitySettings,
  ActivityType,
  DiagramGeneralSettings,
  FlowlineSettings,
  PositionType,
  ResourceActivityPayload
} from '../diagram-type-styles.model';
import {AGENT_INTERACTION} from '../../../shared/constants/diagram-subtypes.constants';
import {FlowlineForm} from '../flowline-form.enum';
import {MAPS_TYPES_ENUM as MAP_TYPES} from '../../../editor-interface/shared/models/map.model';

export class AgentInteractionConfig extends ArchitectureBaseConfig {
  generalSettings(): DiagramGeneralSettings {
    return {
      ...super.generalSettings(),
      tagColor: '#7E2891',
      allowedDrilldownSubtypes: [MAP_TYPES.AGENT_INSTRUCTION, MAP_TYPES.AGENT_INTERACTION],
      disableSubtypeChange: true
    };
  }

  readonly activityShape = {w: 400, h: 120};

  activitySettings(x?: number, y?: number): ActivitySettings {
    const baseSettings = super.activitySettings(x, y);

    return {
      ...baseSettings,
      text: 'Agent topic name',
      footerText: "<b>Scope: Your job is to ...</b> (define your Agent's Job To Be Done)",
      createActivityWithFlowline: true
    };
  }

  flowlineSettings(x?: number, y?: number, id?: number | string): FlowlineSettings {
    const baseSettings = super.flowlineSettings(x, y, id);
    const defaultFlowlineShape = {w: 400, h: 0};

    return {
      ...baseSettings,
      defaultFlowlineShape,
      config: {
        ...baseSettings.config,
        text:
          'Classification Description: <br>Use this topic when the user wants to [goal or action] ' +
          'or is asking about [problem or need]',
        srcPosition: {
          x: x - 400,
          y: y + 60
        },
        tgtPosition: {
          x: x + 400,
          y
        }
      },
      template: '<div class="interaction-flowline-content">{{content}}</div>',
      form: {
        shape: FlowlineForm.shape.Orthogonal,
        type: FlowlineForm.type.Solid,
        start: FlowlineForm.end.None,
        end: FlowlineForm.end.Classic
      }
    };
  }

  readonly activityTypes: ActivityType[] = [
    {
      tooltip: 'ASIDE_L.AGENT_INTERACTION_DEFAULT_ACTIVITY_TOOLTIP',
      icon: 'activity_icon_v2',
      settings: (x?: number, y?: number) => this.activitySettings(x, y)
    },
    this.getResourceActivityType({
      type: AGENT_INTERACTION.RESOURCE_ACTIVITY_TYPES.AI_AGENT,
      tooltip: 'ASIDE_L.AGENT_INTERACTION_ORCHESTRATOR_TOOLTIP',
      text: '"Orchestrator" Agent',
      footerText:
        "<b>Knows all 'assistants' or 'topics' that are available and their scope. " +
        'Chooses to which one offload the utterance.</b>'
    }),
    this.getResourceActivityType({
      type: AGENT_INTERACTION.RESOURCE_ACTIVITY_TYPES.USER,
      tooltip: 'ASIDE_L.AGENT_INTERACTION_UTTERANCES_TOOLTIP',
      text: 'User utterance',
      footerText: '<b>Question or task communicated by the user</b>',
      style: {
        borderColor: '#cecece'
      }
    }),
    this.getResourceActivityType({
      type: AGENT_INTERACTION.RESOURCE_ACTIVITY_TYPES.FLOW,
      tooltip: 'ASIDE_L.AGENT_INTERACTION_FLOW_TOOLTIP',
      text: 'Flow [name]',
      footerText:
        '<b>Record-triggered, platform-event-triggered or scheduled flow that listens to event changes in Salesforce</b>',
      style: {
        borderColor: '#cecece'
      }
    }),
    this.getResourceActivityType({
      type: AGENT_INTERACTION.RESOURCE_ACTIVITY_TYPES.APEX,
      tooltip: 'ASIDE_L.AGENT_INTERACTION_APEX_TOOLTIP',
      text: 'Apex [name]',
      footerText:
        '<b>Apex-trigger, platform-event-triggered or scheduled apex that listens to event changes in Salesforce</b>',
      style: {
        borderColor: '#cecece'
      }
    })
  ];

  private getResourceActivityType({
    type,
    tooltip,
    text,
    footerText,
    style
  }: ResourceActivityPayload) {
    const {icon} = this.diagramMeta?.defaultResources?.[type] || {};

    return {
      type,
      tooltip,
      img: icon,
      settings: (x?: number, y?: number) => ({
        ...this.resourceActivitySettings(x, y),
        text,
        footerText,
        defaultResources: this.getDefaultResources([type]),
        customStyle: !!style,
        style
      })
    };
  }

  private resourceActivitySettings(x?: number, y?: number): ActivitySettings {
    const resourceActivityShape = {w: 209, h: 192};

    return {
      ...this.activitySettings(x, y),
      createActivityWithFlowline: false,
      defaultActivityShape: resourceActivityShape,
      position: this.getPosition(resourceActivityShape, x, y, PositionType.CENTER),
      dragHandlePosition: this.getPosition(resourceActivityShape, x, y, PositionType.DEFAULT)
    };
  }
}
